import { DestroyRef, Directive, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEqual } from '@zero/shared/util-helper';
import { OrderData, OrderDataHolder, OrderStatus, PushMessageType } from '@zero/shared/util-models';
import { DataService } from '@zero/shared/util-web-app-services';
import { assign, differenceBy, intersectionBy, isEqualWith, pullAllBy } from 'lodash-es';
import { debounceTime, filter } from 'rxjs/operators';

@Directive()
export abstract class OldOrderList implements OnInit {
  public orders: OrderData[] = [];
  public pageSize = 10;

  protected sortDirection: 'asc' | 'desc' = 'desc';
  protected sortAttribute: string;
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly dataService = inject(DataService);

  public ngOnInit(): void {
    this.dataService.selectedCustomerChanged
      .pipe(
        filter((newCust) => !!newCust),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.orders = [];
        this.loadOrders(true);
      });
    this.dataService.msgSubject
      .pipe(
        filter((msg) => msg && msg.type === PushMessageType.order),
        debounceTime(1000),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.loadOrders(false);
      });
  }

  private comparatorForMerge = (o1: OrderData, o2: OrderData): boolean =>
    isEqual(o1.lastBackendUpdate, o2.lastBackendUpdate) && isEqual(o1.status, o2.status);

  protected mergeLoadedOrders(loadedOrders: OrderData[]): void {
    const mergePropertyName = 'orderId';
    const toRemove = differenceBy(this.orders, loadedOrders, mergePropertyName);

    const toAdd = differenceBy(loadedOrders, this.orders, mergePropertyName);

    pullAllBy(this.orders, toRemove, mergePropertyName);
    this.orders.push(...toAdd);

    const orderIdsToUpdate = intersectionBy(this.orders, loadedOrders, mergePropertyName).map((ord) => ord[mergePropertyName]);
    this.orders.forEach((currOrd) => {
      if (orderIdsToUpdate.indexOf(currOrd[mergePropertyName]) > -1) {
        const loadedOrder = loadedOrders.find((o) => o[mergePropertyName] === currOrd[mergePropertyName]);
        if (!isEqualWith(currOrd, loadedOrder, this.comparatorForMerge)) {
          assign(currOrd, loadedOrder);
        }
      }
    });
  }

  protected abstract loadOrders(sorted: boolean): void;

  protected loadExecutedOrders(): Promise<OrderDataHolder> {
    return this.dataService.loadOrdersHttp(OrderStatus.Filled, this.pageSize, this.sortAttribute, this.sortDirection);
  }

  protected loadCanceledOrders(): Promise<OrderDataHolder> {
    return this.dataService.loadOrdersHttp(OrderStatus.Canceled, this.pageSize, this.sortAttribute, this.sortDirection);
  }

  public showMore(): void {
    this.pageSize += 10;
    this.loadOrders(false);
  }
}
